/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import "./Pledges.css";
import { Heading, Table, Text, Badge } from "@radix-ui/themes";
import { GeneralContextFunc } from "../../Context/GeneralContex";
import { getSuperAdminPledges, getSuperAdminPledgeDetails, distributePledgeFunds, getCampaignList } from "../../Services/DashboardApi";
import { formatTimestamp } from "../../utils/functions";

const INITIAL_FILTERS = {
  status: "ALL",
  frequency: "ALL",
  donorName: "",
  startDate: "",
  endDate: "",
};

const FREQUENCY_OPTIONS = [
  { label: "All frequencies", value: "ALL" },
  { label: "One-Time", value: "ONE_TIME" },
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Monthly", value: "MONTHLY" },
];

const STATUS_OPTIONS = [
  { label: "All statuses", value: "ALL" },
  { label: "Pending", value: "pending" },
  { label: "Active", value: "active" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Past Due", value: "past_due" },
  { label: "Payment Failed", value: "payment_failed" },
  { label: "Paused", value: "paused" },
];

const statusTone = (status = "") => {
  const normalized = status.toLowerCase();
  switch (normalized) {
    case "active":
      return "green";
    case "pending":
      return "amber";
    case "completed":
      return "blue";
    case "cancelled":
    case "canceled":
    case "payment_failed":
      return "red";
    case "past_due":
      return "orange";
    case "paused":
      return "gray";
    default:
      return "gray";
  }
};

const emptyDistributionRow = () => ({
  campaignId: "",
  amount: "",
  notes: "",
});

const Pledges = () => {
  const { setloader, initAlert } = GeneralContextFunc();

  const [filtersForm, setFiltersForm] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [pledges, setPledges] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [distributionDrafts, setDistributionDrafts] = useState([emptyDistributionRow()]);
  const [distributionSubmitting, setDistributionSubmitting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignModalIndex, setCampaignModalIndex] = useState(null);
  const [expandedCampaignId, setExpandedCampaignId] = useState(null);

  useEffect(() => {
    fetchPledges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const fetchPledges = () => {
    setloader(true);
    getSuperAdminPledges(appliedFilters)
      .then((response) => {
        setMetrics(response.metrics || null);
        setPledges(response.pledges || []);
      })
      .catch((error) => {
        console.error("Failed to load pledges", error);
        const message = error?.response?.data?.message ?? "Failed to load pledges. Please retry.";
        initAlert(message, "error");
      })
      .finally(() => {
        setloader(false);
      });
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filtersForm);
  };

  const handleResetFilters = () => {
    setFiltersForm(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
  };

  const handleFilterChange = (field, value) => {
    setFiltersForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      // Fetch approved campaigns for distribution
      const response = await getCampaignList({
        page: 1,
        limit: 1000, // Get all campaigns for dropdown
        status: "Approved",
      });
      // API returns campaigns in paginatedData.resourceData
      const campaignsList = response?.paginatedData?.resourceData || response?.resourceData || [];
      console.log("Fetched campaigns:", campaignsList.length);
      setCampaigns(campaignsList);
    } catch (error) {
      console.error("Failed to load campaigns", error);
      initAlert("Failed to load campaigns. You can still enter campaign ID manually.", "warning");
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const handleOpenPledge = async (pledgeId) => {
    setDetailLoading(true);
    setSelectedDetail(null);
    try {
      const detail = await getSuperAdminPledgeDetails(pledgeId);
      setSelectedDetail(detail);
      setDistributionDrafts([emptyDistributionRow()]);
      // Fetch campaigns when opening pledge detail
      await fetchCampaigns();
    } catch (error) {
      console.error("Failed to load pledge detail", error);
      const message = error?.response?.data?.message ?? "Unable to load pledge details.";
      initAlert(message, "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedDetail(null);
    setDistributionDrafts([emptyDistributionRow()]);
  };

  const formatCurrency = (amount = 0) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);

  const formatLocation = (location) => {
    if (!location) return "N/A";
    if (typeof location === "string") return location;
    if (typeof location === "object") {
      const parts = [];
      if (location.city) parts.push(location.city);
      if (location.state) parts.push(location.state);
      if (location.country) parts.push(location.country);
      return parts.length > 0 ? parts.join(", ") : "N/A";
    }
    return "N/A";
  };

  const openCampaignModal = (index) => {
    setCampaignModalIndex(index);
    setCampaignModalOpen(true);
    setExpandedCampaignId(null);
  };

  const closeCampaignModal = () => {
    setCampaignModalOpen(false);
    setCampaignModalIndex(null);
    setExpandedCampaignId(null);
  };

  const selectCampaign = (campaignId) => {
    if (campaignModalIndex !== null) {
      handleDistributionChange(campaignModalIndex, "campaignId", campaignId);
      closeCampaignModal();
    }
  };

  const getCampaignImage = (campaign) => {
    if (campaign?.designAssets?.coverImage) return campaign.designAssets.coverImage;
    if (campaign?.image) return campaign.image;
    if (campaign?.url) return campaign.url;
    return "/logo_rafl.png";
  };

  const getCampaignGoal = (campaign) => {
    let totalFees = 0;

    // Add campaign fees
    if (campaign?.campaignFee) {
      Object.values(campaign.campaignFee).forEach((feeItem) => {
        if (feeItem && typeof feeItem === "object" && feeItem.amount) {
          totalFees += feeItem.amount;
        } else if (typeof feeItem === "number" && !isNaN(feeItem)) {
          totalFees += feeItem;
        }
      });
    }

    // Add all goal costs
    if (campaign?.goals && Array.isArray(campaign.goals) && campaign.goals.length > 0) {
      campaign.goals.forEach((goal) => {
        totalFees += Number(goal.approxCost) || 0;
      });
    }

    // Fallback to totalApproxCost if no goals/fees
    if (totalFees === 0 && campaign?.totalApproxCost) {
      totalFees = Number(campaign.totalApproxCost) || 0;
    }

    return totalFees || 0;
  };

  const getCampaignProgress = (campaign) => {
    const goal = getCampaignGoal(campaign) || 1;
    const raised = campaign?.amountRaised || campaign?.totalDonationAmt || 0;
    return Math.min((raised / goal) * 100, 100);
  };

  const filteredSummary = useMemo(() => {
    if (!metrics) return [];
    return [
      {
        label: "Total pledges",
        value: metrics.totalPledges || 0,
        helper: `${metrics.recurringPledges || 0} recurring`,
        tone: "primary",
      },
      {
        label: "Active pledges",
        value: metrics.activePledges || 0,
        helper: `${metrics.atRiskPledges || 0} at risk`,
        tone: "success",
      },
      {
        label: "Total collected",
        value: formatCurrency(metrics.totalAmountCollected),
        helper: "Captured via Stripe",
        tone: "brand",
      },
      {
        label: "Available to distribute",
        value: formatCurrency(metrics.totalAvailableBalance),
        helper: formatCurrency(metrics.totalDistributed) + " distributed",
        tone: "accent",
      },
    ];
  }, [metrics]);

  const payments = selectedDetail?.payments || [];
  const distributions = selectedDetail?.distributions || [];

  const getCampaignRemainingGoal = (campaignId) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (!campaign) return null;
    const goal = getCampaignGoal(campaign);
    const raised = campaign?.amountRaised || campaign?.totalDonationAmt || 0;
    const remaining = Math.max(0, goal - raised);
    return remaining;
  };

  const handleDistributionChange = (index, field, value) => {
    setDistributionDrafts((prev) => {
      const updated = prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry));

      // Validate amount against remaining goal if campaign is selected
      if (field === "amount" && updated[index].campaignId) {
        const remainingGoal = getCampaignRemainingGoal(updated[index].campaignId);
        if (remainingGoal !== null) {
          const amount = Number(value) || 0;
          if (amount > remainingGoal) {
            // Don't update if it exceeds remaining goal
            return prev;
          }
        }
      }

      return updated;
    });
  };

  const handleAddDistributionRow = () => {
    setDistributionDrafts((prev) => [...prev, emptyDistributionRow()]);
  };

  const handleRemoveDistributionRow = (index) => {
    setDistributionDrafts((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const sanitizeDistributions = () => {
    return distributionDrafts
      .map((entry) => ({
        campaignId: entry.campaignId.trim(),
        amount: Number(entry.amount),
        notes: entry.notes?.trim() || "",
      }))
      .filter((entry) => entry.campaignId && entry.amount > 0);
  };

  const handleSubmitDistribution = async () => {
    if (!selectedDetail) return;
    const sanitized = sanitizeDistributions();

    if (sanitized.length === 0) {
      initAlert("Please add at least one valid distribution entry.", "warning");
      return;
    }

    const totalRequested = sanitized.reduce((acc, item) => acc + item.amount, 0);
    const available = selectedDetail.summary?.availableBalance || 0;

    if (totalRequested > available) {
      initAlert("Distribution total exceeds available pledge balance.", "warning");
      return;
    }

    // Check for duplicate campaign IDs
    const campaignIds = sanitized.map((item) => item.campaignId);
    const uniqueCampaignIds = new Set(campaignIds);
    if (campaignIds.length !== uniqueCampaignIds.size) {
      initAlert("Each campaign can only appear once per distribution. Please remove duplicate campaign selections.", "warning");
      return;
    }

    // Validate each distribution doesn't exceed campaign's remaining goal
    for (const item of sanitized) {
      const remainingGoal = getCampaignRemainingGoal(item.campaignId);
      if (remainingGoal !== null && item.amount > remainingGoal) {
        const campaign = campaigns.find((c) => c.id === item.campaignId);
        const campaignTitle = campaign?.title || item.campaignId;
        initAlert(
          `Distribution amount (${formatCurrency(item.amount)}) exceeds the remaining goal (${formatCurrency(
            remainingGoal
          )}) for campaign "${campaignTitle}".`,
          "warning"
        );
        return;
      }
    }

    setDistributionSubmitting(true);
    try {
      const response = await distributePledgeFunds({
        pledgeId: selectedDetail.pledge.id,
        distributions: sanitized,
      });

      initAlert(response?.message || "Funds distributed successfully.", "success");
      setSelectedDetail((prev) => {
        if (!prev) return prev;
        const updatedSummary = {
          ...prev.summary,
          totalDistributed: response.pledge.totalDistributed,
          availableBalance: response.pledge.availableBalance,
        };
        return {
          ...prev,
          pledge: response.pledge,
          summary: updatedSummary,
          distributions: [...(response.distributions || []), ...(prev.distributions || [])],
        };
      });
      setDistributionDrafts([emptyDistributionRow()]);
      fetchPledges();
    } catch (error) {
      console.error("Failed to distribute pledge funds", error);
      const message = error?.response?.data?.message ?? "Unable to distribute pledge funds. Please try again.";
      initAlert(message, "error");
    } finally {
      setDistributionSubmitting(false);
    }
  };

  return (
    <div className="pledges-page">
      <Heading align="left" mb="4" mt="5" mx="5" size="8" weight="bold">
        Pledges
      </Heading>

      <section className="pledges-summary" aria-label="Pledge overview">
        {filteredSummary.map((card) => (
          <div key={card.label} className={`pledges-summary-card pledges-summary-${card.tone}`}>
            <p className="pledges-summary-label">{card.label}</p>
            <p className="pledges-summary-value">{card.value}</p>
            <span className="pledges-summary-helper">{card.helper}</span>
          </div>
        ))}
      </section>

      <section className="pledges-filters" aria-label="Filter pledges">
        <div className="pledges-filter-grid">
          <div className="pledges-filter-field">
            <label htmlFor="pledge-donor">Donor</label>
            <input
              id="pledge-donor"
              type="text"
              placeholder="Search by name or email"
              value={filtersForm.donorName}
              onChange={(event) => handleFilterChange("donorName", event.target.value)}
            />
          </div>
          <div className="pledges-filter-field">
            <label htmlFor="pledge-status">Status</label>
            <select id="pledge-status" value={filtersForm.status} onChange={(event) => handleFilterChange("status", event.target.value)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="pledges-filter-field">
            <label htmlFor="pledge-frequency">Frequency</label>
            <select id="pledge-frequency" value={filtersForm.frequency} onChange={(event) => handleFilterChange("frequency", event.target.value)}>
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="pledges-filter-field">
            <label htmlFor="pledge-start">Start date</label>
            <input
              id="pledge-start"
              type="date"
              value={filtersForm.startDate}
              onChange={(event) => handleFilterChange("startDate", event.target.value)}
            />
          </div>
          <div className="pledges-filter-field">
            <label htmlFor="pledge-end">End date</label>
            <input id="pledge-end" type="date" value={filtersForm.endDate} onChange={(event) => handleFilterChange("endDate", event.target.value)} />
          </div>
        </div>
        <div className="pledges-filter-actions">
          <button className="pledges-button primary" onClick={handleApplyFilters}>
            Apply filters
          </button>
          <button className="pledges-button surface" onClick={handleResetFilters}>
            Reset
          </button>
        </div>
      </section>

      <section className="pledges-table-section">
        <div className="pledges-table-header">
          <Text size="4" weight="bold">
            {pledges.length} pledges
          </Text>
        </div>
        <div className="pledges-table-wrapper">
          <Table.Root variant="surface" size="3">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Pledge</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Donor</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Frequency</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Collected</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Available</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Updated</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell align="right">Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pledges.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <div className="pledges-empty-state">
                      <Text color="gray">No pledges match your filters.</Text>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
              {pledges.map((pledge) => (
                <Table.Row key={pledge.id}>
                  <Table.Cell>
                    <div className="pledges-cell-main">
                      <strong>{pledge.referenceCode}</strong>
                      <span className="pledges-cell-subtle">Stripe session: {pledge.stripeSessionId || "—"}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="pledges-cell-main">
                      <strong>{pledge?.donor?.fullName || "—"}</strong>
                      <span className="pledges-cell-subtle">{pledge?.donor?.email || "—"}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color="gray" variant="soft">
                      {pledge.frequency.replace("_", " ").toLowerCase()}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={statusTone(pledge.status)} variant="soft">
                      {pledge.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{formatCurrency(pledge.totalCollected)}</Table.Cell>
                  <Table.Cell>{formatCurrency(pledge.availableBalance)}</Table.Cell>
                  <Table.Cell>{formatTimestamp(pledge.updatedAt || pledge.createdAt)}</Table.Cell>
                  <Table.Cell align="right">
                    <button className="pledges-button ghost" onClick={() => handleOpenPledge(pledge.id)}>
                      View
                    </button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </div>
      </section>

      {(detailLoading || selectedDetail) && (
        <div className="pledges-detail-overlay">
          <div className="pledges-detail-panel">
            {detailLoading ? (
              <div className="pledges-detail-loading">
                <div className="pledges-spinner" aria-hidden />
                <p>Loading pledge details…</p>
              </div>
            ) : (
              selectedDetail && (
                <>
                  <div className="pledges-detail-header">
                    <div>
                      <h2>{selectedDetail.pledge.referenceCode}</h2>
                      <p>
                        Created {formatTimestamp(selectedDetail.pledge.createdAt)} · Last charge{" "}
                        {selectedDetail.summary?.lastChargeDate ? formatTimestamp(selectedDetail.summary.lastChargeDate) : "—"}
                      </p>
                    </div>
                    <button className="pledges-close-button" onClick={handleCloseDetail}>
                      Close
                    </button>
                  </div>

                  <div className="pledges-detail-grid">
                    <div className="pledges-detail-card">
                      <h3>Donor</h3>
                      <p className="pledges-detail-primary">{selectedDetail.pledge?.donor?.fullName || "Unknown donor"}</p>
                      <p className="pledges-detail-subtle">{selectedDetail.pledge?.donor?.email || "No email available"}</p>
                      {selectedDetail.pledge?.donor?.phone && <p className="pledges-detail-subtle">{selectedDetail.pledge.donor.phone}</p>}
                    </div>

                    <div className="pledges-detail-card">
                      <h3>Pledge profile</h3>
                      <ul>
                        <li>
                          Frequency: <strong>{selectedDetail.pledge.frequency.replace("_", " ").toLowerCase()}</strong>
                        </li>
                        <li>
                          Status:{" "}
                          <Badge color={statusTone(selectedDetail.pledge.status)} variant="soft">
                            {selectedDetail.pledge.status}
                          </Badge>
                        </li>
                        <li>Stripe customer: {selectedDetail.pledge.stripeCustomerId || "—"}</li>
                        {selectedDetail.pledge.isRecurring && <li>Next charge: {formatTimestamp(selectedDetail.pledge.nextChargeDate)}</li>}
                      </ul>
                    </div>
                  </div>
                  <div className="pledges-detail-card" style={{ gridColumn: "1 / -1" }}>
                    <h3>Performance</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem", marginTop: "1rem" }}>
                      <div style={{ padding: "1rem", backgroundColor: "#F7FBFA", borderRadius: "12px", border: "1px solid #E5F0EC" }}>
                        <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "#2F6A61", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                          Total Collected
                        </p>
                        <p style={{ fontSize: "1.5rem", fontWeight: 700, color: "#203840" }}>
                          {formatCurrency(selectedDetail.summary.totalCollected)}
                        </p>
                        <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>All payments received</p>
                      </div>
                      <div style={{ padding: "1rem", backgroundColor: "#F5F3FF", borderRadius: "12px", border: "1px solid #EDE7FE" }}>
                        <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b46c1", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                          Total Distributed
                        </p>
                        <p style={{ fontSize: "1.5rem", fontWeight: 700, color: "#4c1d95" }}>
                          {formatCurrency(selectedDetail.summary.totalDistributed)}
                        </p>
                        <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>Allocated to campaigns</p>
                      </div>
                      <div style={{ padding: "1rem", backgroundColor: "#FFFBEB", borderRadius: "12px", border: "1px solid #FDE68A" }}>
                        <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "#B45309", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                          Available Balance
                        </p>
                        <p style={{ fontSize: "1.5rem", fontWeight: 700, color: "#92400E" }}>
                          {formatCurrency(selectedDetail.summary.availableBalance)}
                        </p>
                        <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>Ready to distribute</p>
                      </div>
                      {selectedDetail.pledge.isRecurring && selectedDetail.summary.nextChargeDate && (
                        <div style={{ padding: "1rem", backgroundColor: "#F9FAFB", borderRadius: "12px", border: "1px solid #E5E7EB" }}>
                          <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                            Next Charge
                          </p>
                          <p style={{ fontSize: "1rem", fontWeight: 600, color: "#203840" }}>
                            {formatTimestamp(selectedDetail.summary.nextChargeDate)}
                          </p>
                          <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>
                            {selectedDetail.pledge.frequency.replace("_", " ")} recurring
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pledges-section">
                    <div className="pledges-section-header">
                      <h3>Recent payments</h3>
                      <Badge variant="soft" color="gray">
                        {payments.length} records
                      </Badge>
                    </div>
                    <div className="pledges-timeline">
                      {payments.length === 0 ? (
                        <p className="pledges-placeholder">No payments yet.</p>
                      ) : (
                        payments.map((payment) => (
                          <div key={payment.id} className="pledges-timeline-item">
                            <div>
                              <p className="pledges-timeline-title">
                                {formatCurrency(payment.amount)} – {payment.status}
                              </p>
                              <p className="pledges-timeline-subtle">Processed {formatTimestamp(payment.processedAt)}</p>
                            </div>
                            <div className="pledges-timeline-meta">
                              <span>{payment.eventType || "Stripe"}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="pledges-section">
                    <div className="pledges-section-header">
                      <h3>Distributions</h3>
                      <Badge variant="soft" color="gray">
                        {distributions.length} records
                      </Badge>
                    </div>
                    <div className="pledges-timeline">
                      {distributions.length === 0 ? (
                        <p className="pledges-placeholder">Funds have not been allocated yet. Use the form below to distribute.</p>
                      ) : (
                        distributions.map((distribution) => (
                          <div key={distribution.id} className="pledges-timeline-item">
                            <div>
                              <p className="pledges-timeline-title">
                                {formatCurrency(distribution.amount)} → {distribution.campaign?.title || distribution.campaignId}
                              </p>
                              <p className="pledges-timeline-subtle">
                                Distributed {formatTimestamp(distribution.distributedAt)} · Notes: {distribution.notes || "—"}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="pledges-section">
                    <div className="pledges-section-header">
                      <h3>Distribute available balance</h3>
                      <Badge variant="soft" color="blue">
                        Available {formatCurrency(selectedDetail.summary.availableBalance)}
                      </Badge>
                    </div>
                    {(() => {
                      const availableBalance = Number(selectedDetail?.summary?.availableBalance || 0);
                      const hasBalance = availableBalance > 0;

                      if (!hasBalance) {
                        return (
                          <div style={{ padding: "1.5rem", textAlign: "center", color: "#6b7280" }}>
                            <p>No available balance to distribute. This pledge has been fully distributed.</p>
                          </div>
                        );
                      }

                      return (
                        <div className="pledges-distribution-form">
                          {distributionDrafts.map((entry, index) => (
                            <div key={index} className="pledges-distribution-row">
                              <div className="pledges-distribution-field">
                                <label htmlFor={`campaign-${index}`}>Campaign</label>
                                {entry.campaignId ? (
                                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                    <input id={`campaign-${index}`} type="text" value={entry.campaignId} readOnly style={{ flex: 1 }} />
                                    <button
                                      type="button"
                                      onClick={() => openCampaignModal(index)}
                                      disabled={!hasBalance}
                                      style={{
                                        padding: "0.5rem 1rem",
                                        backgroundColor: !hasBalance ? "#9ca3af" : "#203840",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: !hasBalance ? "not-allowed" : "pointer",
                                        fontSize: "0.9rem",
                                        opacity: !hasBalance ? 0.6 : 1,
                                      }}
                                      title={!hasBalance ? "No available balance to distribute" : ""}
                                    >
                                      Change
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openCampaignModal(index)}
                                    disabled={!hasBalance}
                                    style={{
                                      width: "100%",
                                      padding: "0.75rem",
                                      backgroundColor: !hasBalance ? "#9ca3af" : "#203840",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "12px",
                                      cursor: !hasBalance ? "not-allowed" : "pointer",
                                      fontSize: "0.95rem",
                                      fontWeight: 500,
                                      opacity: !hasBalance ? 0.6 : 1,
                                    }}
                                    title={!hasBalance ? "No available balance to distribute" : ""}
                                  >
                                    {campaignsLoading ? "Loading campaigns..." : "Select Campaign"}
                                  </button>
                                )}
                              </div>
                              <div className="pledges-distribution-field">
                                <label htmlFor={`amount-${index}`}>
                                  Amount (USD)
                                  {entry.campaignId &&
                                    (() => {
                                      const remainingGoal = getCampaignRemainingGoal(entry.campaignId);
                                      if (remainingGoal !== null && remainingGoal > 0) {
                                        return (
                                          <span style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: "normal", marginLeft: "0.5rem" }}>
                                            (Max: {formatCurrency(remainingGoal)})
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                </label>
                                <input
                                  id={`amount-${index}`}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  max={entry.campaignId ? getCampaignRemainingGoal(entry.campaignId) || undefined : undefined}
                                  value={entry.amount}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    if (entry.campaignId) {
                                      const remainingGoal = getCampaignRemainingGoal(entry.campaignId);
                                      if (remainingGoal !== null) {
                                        const amount = Number(value) || 0;
                                        if (amount > remainingGoal) {
                                          initAlert(`Amount cannot exceed the remaining goal of ${formatCurrency(remainingGoal)}.`, "warning");
                                          return;
                                        }
                                      }
                                    }
                                    handleDistributionChange(index, "amount", value);
                                  }}
                                />
                                {entry.campaignId &&
                                  entry.amount &&
                                  (() => {
                                    const remainingGoal = getCampaignRemainingGoal(entry.campaignId);
                                    if (remainingGoal !== null) {
                                      const amount = Number(entry.amount) || 0;
                                      if (amount > remainingGoal) {
                                        return (
                                          <span style={{ fontSize: "0.75rem", color: "#ef4444", marginTop: "0.25rem", display: "block" }}>
                                            Amount exceeds remaining goal of {formatCurrency(remainingGoal)}
                                          </span>
                                        );
                                      }
                                    }
                                    return null;
                                  })()}
                              </div>
                              <div className="pledges-distribution-field">
                                <label htmlFor={`notes-${index}`}>Notes</label>
                                <input
                                  id={`notes-${index}`}
                                  type="text"
                                  placeholder="Optional"
                                  value={entry.notes}
                                  onChange={(event) => handleDistributionChange(index, "notes", event.target.value)}
                                />
                              </div>
                              <div className="pledges-distribution-actions">
                                <button
                                  type="button"
                                  className="pledges-button ghost"
                                  onClick={() => handleRemoveDistributionRow(index)}
                                  disabled={distributionDrafts.length === 1}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                          <div className="pledges-distribution-footer">
                            <button type="button" className="pledges-button surface" onClick={handleAddDistributionRow}>
                              Add another campaign
                            </button>
                            <button
                              type="button"
                              className="pledges-button primary"
                              onClick={handleSubmitDistribution}
                              disabled={distributionSubmitting}
                            >
                              {distributionSubmitting ? "Distributing…" : "Distribute funds"}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )
            )}
          </div>
        </div>
      )}

      {/* Campaign Selection Modal */}
      {campaignModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "2rem",
          }}
          onClick={closeCampaignModal}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "18px",
              width: "100%",
              maxWidth: "1200px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "1.5rem 2rem",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#101828", margin: 0 }}>Select Campaign</h2>
              <button
                onClick={closeCampaignModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "#6b7280",
                  padding: "0.5rem",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div
              style={{
                padding: "2rem",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {campaignsLoading ? (
                <div style={{ textAlign: "center", padding: "3rem" }}>
                  <div
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      border: "3px solid #f3f4f6",
                      borderTopColor: "#203840",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <p style={{ marginTop: "1rem", color: "#6b7280" }}>Loading campaigns...</p>
                </div>
              ) : campaigns.length === 0 ? (
                <div style={{ textAlign: "center", padding: "3rem" }}>
                  <p style={{ color: "#6b7280" }}>No approved campaigns available.</p>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  {campaigns.map((campaign) => {
                    const progress = getCampaignProgress(campaign);
                    const isExpanded = expandedCampaignId === campaign.id;
                    const goal = getCampaignGoal(campaign);
                    const raised = campaign?.amountRaised || campaign?.totalDonationAmt || 0;

                    // Check if this campaign is already selected in another distribution row
                    const isAlreadySelected = distributionDrafts.some((entry, idx) => entry.campaignId === campaign.id && idx !== campaignModalIndex);

                    return (
                      <div
                        key={campaign.id}
                        style={{
                          backgroundColor: "white",
                          borderRadius: "16px",
                          border: "1px solid #e5e7eb",
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          boxShadow: isExpanded ? "0 10px 25px rgba(0, 0, 0, 0.1)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        {/* Campaign Image */}
                        <div
                          style={{
                            height: "180px",
                            backgroundColor: "#f3f4f6",
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={getCampaignImage(campaign)}
                            alt={campaign.title || "Campaign"}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src = "/logo_rafl.png";
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: "12px",
                              left: "12px",
                              backgroundColor: progress >= 100 ? "#10b981" : "#3b82f6",
                              color: "white",
                              padding: "4px 12px",
                              borderRadius: "20px",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          >
                            {progress >= 100 ? "Completed" : "Active"}
                          </div>
                          {isAlreadySelected && (
                            <div
                              style={{
                                position: "absolute",
                                top: "12px",
                                right: "12px",
                                backgroundColor: "#f59e0b",
                                color: "white",
                                padding: "4px 12px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                              }}
                            >
                              Already Selected
                            </div>
                          )}
                        </div>

                        {/* Campaign Content */}
                        <div style={{ padding: "1.25rem" }}>
                          {/* Category */}
                          <div
                            style={{
                              display: "inline-block",
                              backgroundColor: "#f3f4f6",
                              color: "#374151",
                              padding: "4px 12px",
                              borderRadius: "20px",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              marginBottom: "0.75rem",
                            }}
                          >
                            {campaign.category || "Uncategorized"}
                          </div>

                          {/* Title */}
                          <h3
                            style={{
                              fontSize: "1.1rem",
                              fontWeight: 700,
                              color: "#101828",
                              margin: "0 0 0.5rem 0",
                              lineHeight: 1.3,
                            }}
                          >
                            {campaign.title || "Untitled Campaign"}
                          </h3>

                          {/* Location */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              color: "#6b7280",
                              fontSize: "0.875rem",
                              marginBottom: "1rem",
                            }}
                          >
                            <span>📍</span>
                            <span>{formatLocation(campaign.location)}</span>
                          </div>

                          {/* Progress */}
                          <div style={{ marginBottom: "1rem" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "0.875rem",
                                marginBottom: "0.5rem",
                              }}
                            >
                              <span style={{ fontWeight: 700, color: "#101828" }}>{formatCurrency(raised)} raised</span>
                              <span style={{ color: "#6b7280" }}>{formatCurrency(goal)} goal</span>
                            </div>
                            <div
                              style={{
                                width: "100%",
                                height: "8px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "4px",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  width: `${progress}%`,
                                  height: "100%",
                                  backgroundColor: progress >= 100 ? "#10b981" : "#3b82f6",
                                  transition: "width 0.3s ease",
                                }}
                              />
                            </div>
                            <div
                              style={{
                                textAlign: "right",
                                fontSize: "0.75rem",
                                color: "#6b7280",
                                marginTop: "0.25rem",
                              }}
                            >
                              {Math.round(progress)}% funded
                            </div>
                          </div>

                          {/* Expandable Details */}
                          {isExpanded && (
                            <div
                              style={{
                                marginTop: "1rem",
                                paddingTop: "1rem",
                                borderTop: "1px solid #e5e7eb",
                                animation: "fadeIn 0.3s ease",
                              }}
                            >
                              {/* Description */}
                              {campaign.cause && (
                                <div style={{ marginBottom: "1rem" }}>
                                  <h4
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#374151",
                                      marginBottom: "0.5rem",
                                    }}
                                  >
                                    Description
                                  </h4>
                                  <p
                                    style={{
                                      fontSize: "0.875rem",
                                      color: "#6b7280",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {campaign.cause}
                                  </p>
                                </div>
                              )}

                              {/* Goals */}
                              {campaign.goals && campaign.goals.length > 0 && (
                                <div style={{ marginBottom: "1rem" }}>
                                  <h4
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#374151",
                                      marginBottom: "0.5rem",
                                    }}
                                  >
                                    Goals
                                  </h4>
                                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                    {campaign.goals.map((goal, idx) => (
                                      <li
                                        key={idx}
                                        style={{
                                          fontSize: "0.875rem",
                                          color: "#6b7280",
                                          marginBottom: "0.25rem",
                                          paddingLeft: "1rem",
                                          position: "relative",
                                        }}
                                      >
                                        <span
                                          style={{
                                            position: "absolute",
                                            left: 0,
                                            top: "0.4rem",
                                            width: "6px",
                                            height: "6px",
                                            backgroundColor: "#3b82f6",
                                            borderRadius: "50%",
                                          }}
                                        />
                                        {goal.title || goal.name || "Goal"} - {formatCurrency(goal.approxCost || 0)}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Contact Info */}
                              {campaign.contactDetails && (
                                <div style={{ marginBottom: "1rem" }}>
                                  <h4
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#374151",
                                      marginBottom: "0.5rem",
                                    }}
                                  >
                                    Contact
                                  </h4>
                                  <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: 0 }}>{campaign.contactDetails.name || "N/A"}</p>
                                  {campaign.contactDetails.email && (
                                    <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: "0.25rem 0 0 0" }}>{campaign.contactDetails.email}</p>
                                  )}
                                </div>
                              )}

                              {/* Campaign ID */}
                              <div
                                style={{
                                  padding: "0.75rem",
                                  backgroundColor: "#f9fafb",
                                  borderRadius: "8px",
                                  marginBottom: "1rem",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    marginBottom: "0.25rem",
                                  }}
                                >
                                  Campaign ID
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#101828",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {campaign.id}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              type="button"
                              onClick={() => setExpandedCampaignId(isExpanded ? null : campaign.id)}
                              style={{
                                flex: 1,
                                padding: "0.625rem",
                                backgroundColor: "#f3f4f6",
                                color: "#374151",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => (e.target.style.backgroundColor = "#e5e7eb")}
                              onMouseLeave={(e) => (e.target.style.backgroundColor = "#f3f4f6")}
                            >
                              {isExpanded ? "Hide Details" : "See Details"}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (!isAlreadySelected && progress < 100) {
                                  selectCampaign(campaign.id);
                                }
                              }}
                              disabled={progress >= 100 || isAlreadySelected}
                              style={{
                                flex: 1,
                                padding: "0.625rem",
                                backgroundColor: progress >= 100 || isAlreadySelected ? "#9ca3af" : "#203840",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: progress >= 100 || isAlreadySelected ? "not-allowed" : "pointer",
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                transition: "background-color 0.2s",
                                opacity: progress >= 100 || isAlreadySelected ? 0.6 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (progress < 100 && !isAlreadySelected) {
                                  e.target.style.backgroundColor = "#152529";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (progress < 100 && !isAlreadySelected) {
                                  e.target.style.backgroundColor = "#203840";
                                }
                              }}
                              title={
                                progress >= 100
                                  ? "Campaign goal has been reached"
                                  : isAlreadySelected
                                  ? "This campaign is already selected in another distribution row"
                                  : ""
                              }
                            >
                              {progress >= 100 ? "Goal Reached" : isAlreadySelected ? "Already Selected" : "Select"}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add CSS for animations */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Pledges;
